/**
 * shared/icons.ts —— 图标常量 IC / EIC + 侧栏 NAV 路由表
 * 来历：一期 [SEC:icons] 段【逐字节平移】，只加了类型标注。
 *
 * ⚠ 这些 SVG 的尺寸与描边宽度与 15 段样式是配套的（侧栏 17px/1.65px、空态 48px/1.65px），
 *   改任何一个数字都会让图标与文字基线对不齐。要加新图标，照着同组的参数写。
 * ⚠ 【不做循环 / 呼吸动画】（红线 1，用户明确要求）——图标里不许出现 animate / @keyframes。
 */

/** 16px 行内图标 */
export const IC = {
  check:
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3.6 8.4 6.6 11.4 12.4 5"/></svg>',
  warn: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.9 2 13.1h12z"/><path d="M8 6.6v3"/><circle cx="8" cy="11.4" r=".7" fill="currentColor" stroke="none"/></svg>',
  /* 中性提示图标：用于「本期未提交」等草稿态，不含受限 / 加锁语义 */
  info: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="5.7"/><path d="M8 7.4v3.4"/><circle cx="8" cy="5.2" r=".75" fill="currentColor" stroke="none"/></svg>',
  /* AI 助手（2026-08-23）：四芒星 + 一颗卫星点，**填充型**、自带暖色渐变。
     ⚠ 与 IC 里其余描边图标不同，它不吃 `stroke:currentColor`——它是品牌亮点，配色固定。
     ⚠ 渐变 id 全站唯一（`icAi`）：同一个 id 出现两次时，后一个会被前一个覆盖。
     ⚠ 起点 2026-08-23 由 `--gold-3` 提亮成 `#FFEFDD`：按钮底色改成暖橙淡底后，
     原来的金起点与底色明度太近、图标糊在底上。终点用 `--pi-1` 而不是 `--pi-2`，同理。 */
  ai:
    '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">' +
    '<defs><linearGradient id="icAi" x1="0" y1="0" x2="1" y2="1">' +
    '<stop offset="0" style="stop-color:#FFEFDD"/>' +
    '<stop offset=".5" style="stop-color:var(--gold-1)"/>' +
    '<stop offset="1" style="stop-color:var(--pi-1)"/></linearGradient></defs>' +
    '<g fill="url(#icAi)">' +
    '<path d="M13.1 2.2c.2-.6 1-.6 1.2 0l1.05 3.02a1 1 0 0 0 .62.62l3.02 1.05c.6.2.6 1 0 1.2l-3.02 1.05' +
    'a1 1 0 0 0-.62.62l-1.05 3.02c-.2.6-1 .6-1.2 0l-1.05-3.02a1 1 0 0 0-.62-.62L8.4 9.09c-.6-.2-.6-1 0-1.2' +
    'l3.03-1.05a1 1 0 0 0 .62-.62z"/>' +
    '<path d="M6.2 13.4c.13-.4.7-.4.83 0l.5 1.44a1 1 0 0 0 .63.63l1.44.5c.4.13.4.7 0 .83l-1.44.5' +
    'a1 1 0 0 0-.63.63l-.5 1.44c-.13.4-.7.4-.83 0l-.5-1.44a1 1 0 0 0-.63-.63l-1.44-.5c-.4-.13-.4-.7 0-.83' +
    'l1.44-.5a1 1 0 0 0 .63-.63z"/>' +
    '<circle cx="18.6" cy="16.4" r="1.5"/></g></svg>',
  plus: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M8 3.2v9.6M3.2 8h9.6"/></svg>',
  minus:
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M3.2 8h9.6"/></svg>',
  search:
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="7.1" cy="7.1" r="4.3"/><path d="M10.3 10.3 13.6 13.6"/></svg>',
  x: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>',
  chev: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3.5 10.5 8 6 12.5"/></svg>',
  pic: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linejoin="round"><rect x="2.2" y="3.4" width="11.6" height="9.2" rx="1"/><path d="M2.2 10.4 5.6 7.6l2.6 2.1 2.3-1.9 3.3 2.6"/><circle cx="5.9" cy="6.2" r=".95"/></svg>',
  cam: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"><path d="M2 5.4h2.6L5.6 3.6h4.8l1 1.8H14v7H2z"/><circle cx="8" cy="9" r="2.2"/></svg>',
  up: '<svg class="arrow" width="7" height="11" viewBox="0 0 7 11" aria-hidden="true"><path class="up" d="M3.5 0 7 4H0z"/><path class="dn" d="M3.5 11 0 7h7z"/></svg>',
  down: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.6v7.6"/><path d="M5 7.4 8 10.4l3-3"/><path d="M2.8 12.6h10.4"/></svg>',
  /* 上传：与 down 同构、箭头反向，用于管理后台的「批量导入」禁用入口。⚠ 导入一期二期都不做，入口恒禁用 */
  imp: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 10.4V2.8"/><path d="M5 5.8 8 2.8l3 3"/><path d="M2.8 12.6h10.4"/></svg>',
  /* 登录页密码显隐：两枚同构，eyeOff 多一道斜杠 */
  eye: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"><path d="M1.4 8S3.9 3.7 8 3.7 14.6 8 14.6 8 12.1 12.3 8 12.3 1.4 8 1.4 8Z"/><circle cx="8" cy="8" r="2.1"/></svg>',
  eyeOff:
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"><path d="M1.4 8S3.9 3.7 8 3.7 14.6 8 14.6 8 12.1 12.3 8 12.3 1.4 8 1.4 8Z"/><circle cx="8" cy="8" r="2.1"/><path d="M2.6 13.4 13.4 2.6"/></svg>',
  left: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 3.5 5 8l4.5 4.5"/></svg>',
  right:
    '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 3.5 11 8l-4.5 4.5"/></svg>',
} as const;

/* 空态大图标：48px 线性，与侧栏图标同风格（1.65px 描边、--navy-4） */
const eIcon = (d: string): string =>
  `<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

export const EIC = {
  search: eIcon('<circle cx="10.6" cy="10.6" r="6.4"/><path d="M15.3 15.3 20.4 20.4"/>'),
  team: eIcon(
    '<circle cx="9" cy="8" r="3.4"/><path d="M3.4 20.2c0-3.4 2.5-5.8 5.6-5.8s5.6 2.4 5.6 5.8"/><path d="M16.6 5.9a3.1 3.1 0 0 1 0 6.1M17.8 14.9c2.1.6 3.5 2.5 3.5 5"/>',
  ),
  doc: eIcon(
    '<path d="M5.6 3.4h8.2l4.6 4.6v12.6H5.6z"/><path d="M13.8 3.4v4.8h4.6"/><path d="M8.8 12.8h6.4M8.8 16.4h4.4"/>',
  ),
  flag: eIcon('<path d="M6 21V4.2"/><path d="M6 4.6h11.4l-2.1 3.6 2.1 3.6H6"/>'),
  cal: eIcon('<rect x="3.6" y="5.4" width="16.8" height="15" rx="1.4"/><path d="M3.6 10h16.8M8.4 3.4v4M15.6 3.4v4"/>'),
  archive: eIcon(
    '<rect x="3.4" y="4.4" width="17.2" height="4.4" rx="1.2"/><path d="M5.2 8.8v9.6a1.4 1.4 0 0 0 1.4 1.4h10.8a1.4 1.4 0 0 0 1.4-1.4V8.8"/><path d="M10 12.6h4"/>',
  ),
  cam: eIcon('<path d="M2.6 7.4h3.4l1.4-2.6h6.2l1.4 2.6h3.4v12H2.6z"/><circle cx="12" cy="13.2" r="3.4"/>'),
} as const;

/**
 * 侧栏导航：[名称, 图标 path, 路由 hash]
 * 一期七项全部可点；第三项为 null 表示未开发模块（标「后续」角标）。
 * ⚠ 侧栏顺序 = 原始命题的七类模块顺序，不要按开发顺序重排。
 */
export const NAV: ReadonlyArray<readonly [string, string, string | null]> = [
  ['运营总览', '<path d="M2.6 2.6h4.6v4.6H2.6zM9.8 2.6h4.6v3.1H9.8zM2.6 9.8h4.6v4.6H2.6zM9.8 8.3h4.6v6.1H9.8z"/>', '#/'],
  [
    '队伍管理',
    '<circle cx="6.2" cy="5.6" r="2.3"/><path d="M2.4 13.8c0-2.3 1.7-3.9 3.8-3.9s3.8 1.6 3.8 3.9"/><path d="M11.2 4.2a2.1 2.1 0 0 1 0 4.1M12 10.2c1.4.4 2.4 1.7 2.4 3.4"/>',
    '#/teams',
  ],
  ['团队 OKR', '<circle cx="8" cy="8" r="5.6"/><circle cx="8" cy="8" r="2.4"/><path d="M8 2.4v1.6M8 12v1.6M2.4 8H4M12 8h1.6"/>', '#/okr'],
  ['双周报', '<path d="M3.6 2.4h6l3 3v8.2h-9z"/><path d="M9.4 2.4v3.2h3.2"/><path d="M5.7 8.6h4.8M5.7 11h3.2"/>', '#/reports'],
  ['日常打卡', '<path d="M2.2 5.2h2.7l1-1.8h4.6l1 1.8h2.3v8.4H2.2z"/><circle cx="8" cy="9.2" r="2.4"/>', '#/checkins'],
  ['项目快报', '<path d="M2.6 13.4h11"/><path d="M4.6 13.4V8.8M7.6 13.4V4.4M10.6 13.4V10M13.2 13.4V6.6"/>', '#/bulletins'],
  [
    '管理后台',
    '<circle cx="8" cy="8" r="2.5"/><path d="M8 2v1.6M8 12.4V14M2 8h1.6M12.4 8H14M3.9 3.9l1.1 1.1M11 11l1.1 1.1M12.1 3.9 11 5M5 11l-1.1 1.1"/>',
    '#/admin/periods',
  ],
];

export const navIcon = (d: string): string =>
  `<span class="nicon"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">${d}</svg></span>`;
